import { FC, useMemo, useState, useEffect, useCallback } from "react";
import { Stack } from "@mui/material";
import dayjs from "dayjs";
import { Modal } from "@/components/elements/Modal/Modal";
import { Pagination } from "@/components/elements/Pagination";
import { useJobList } from "@/api/services/main/job";
import { PastJobsTable } from "@/features/company/components/company-detail/past-jobs/PastJobsTable";
import { PastJobsTop } from "@/features/company/components/company-detail/past-jobs/PastJobsTop";
import {
  TPastJobsFilterForm,
  TJobDisplayColumnSetting,
  defaultPastJobsDisplayColumns,
  defaultPastJobsFilterValues,
  isEmptyPastJobsFilterValue,
  normalizePastJobsDateFields,
  normalizePastJobsMultiSelectFields,
  stripEmptyPastJobsFilterFields,
  getPastJobsDisplayColumnsKey,
  getPastJobsAdvancedFiltersKey,
  getPastJobsAppliedConditionNameKey,
  getPastJobsAppliedConditionIdKey,
  getPastJobsDefaultSeededKey,
  getPastJobsPageType,
  PAST_JOBS_LOCKED_COLUMN_IDS,
} from "@/features/company/components/company-detail/past-jobs/past-jobs-shared";
import { DATE_FORMAT_FOR_API } from "@/constants/datetime";
import { enforceLockedColumns, resolveSavedConditionColumns } from "@/components/commons/filter-condition/filter-condition-adapter-kit";
import { useSavedConditionList } from "@/api/services/main/saved-condition";
import { readSessionFilterState, writeSessionFilterState } from "@/utils/session-filter-state";

type TPastJobsModalProps = {
  open: boolean;
  onClose: () => void;
  companyId?: number;
};

const PER_PAGE = 20;

const readAppliedConditionName = (companyId?: number) => localStorage.getItem(getPastJobsAppliedConditionNameKey(companyId)) ?? "";

const readAppliedConditionId = (companyId?: number): number | null => {
  const saved = localStorage.getItem(getPastJobsAppliedConditionIdKey(companyId));
  if (!saved) return null;
  const parsed = Number(saved);
  return isNaN(parsed) ? null : parsed;
};

const readAdvancedFilters = (companyId?: number): TPastJobsFilterForm | null => {
  const saved = localStorage.getItem(getPastJobsAdvancedFiltersKey(companyId));
  if (!saved) return null;
  try {
    const parsed = JSON.parse(saved);
    return {
      ...defaultPastJobsFilterValues,
      ...normalizePastJobsMultiSelectFields(normalizePastJobsDateFields(parsed) as Partial<Record<string, unknown>>),
    } as TPastJobsFilterForm;
  } catch {
    return null;
  }
};

const readDisplayColumns = (companyId?: number): TJobDisplayColumnSetting[] | null => {
  const saved = localStorage.getItem(getPastJobsDisplayColumnsKey(companyId));
  if (!saved) return null;
  try {
    return JSON.parse(saved);
  } catch {
    return null;
  }
};

/** Modal listing all jobs for a company with filter/display settings and saved conditions. */
export const PastJobsModal: FC<TPastJobsModalProps> = ({ open, onClose, companyId }) => {
  const [page, setPage] = useState(1);

  const [appliedConditionName, setAppliedConditionName] = useState<string>(() => readAppliedConditionName(companyId));
  const [appliedConditionId, setAppliedConditionId] = useState<number | null>(() => readAppliedConditionId(companyId));
  const [advancedFilters, setAdvancedFilters] = useState<TPastJobsFilterForm | null>(() => readAdvancedFilters(companyId));
  const [displayColumnSettings, setDisplayColumnSettings] = useState<TJobDisplayColumnSetting[] | null>(() => readDisplayColumns(companyId));
  const lockedDisplayColumnSettings = useMemo(
    () => displayColumnSettings && enforceLockedColumns(displayColumnSettings, PAST_JOBS_LOCKED_COLUMN_IDS),
    [displayColumnSettings],
  );

  // Reset page whenever modal re-opens so filter changes outside the modal don't confuse pagination.
  useEffect(() => {
    if (open) setPage(1);
  }, [open]);

  // Reload all per-company state from localStorage whenever the target company switches.
  useEffect(() => {
    setAppliedConditionName(readAppliedConditionName(companyId));
    setAppliedConditionId(readAppliedConditionId(companyId));
    setAdvancedFilters(readAdvancedFilters(companyId));
    setDisplayColumnSettings(readDisplayColumns(companyId));
    setPage(1);
  }, [companyId]);

  const handleAppliedConditionNameChange = useCallback(
    (name: string) => {
      setAppliedConditionName(name);
      const key = getPastJobsAppliedConditionNameKey(companyId);
      if (name) {
        localStorage.setItem(key, name);
      } else {
        localStorage.removeItem(key);
      }
    },
    [companyId],
  );

  const handleAppliedConditionIdChange = useCallback(
    (id: number | null) => {
      setAppliedConditionId(id);
      const key = getPastJobsAppliedConditionIdKey(companyId);
      if (id !== null) {
        localStorage.setItem(key, String(id));
      } else {
        localStorage.removeItem(key);
      }
    },
    [companyId],
  );

  const handleApplyAdvancedFilters = useCallback(
    (filters: TPastJobsFilterForm) => {
      const hasAnyFilter = Object.values(filters).some((v) => !isEmptyPastJobsFilterValue(v));
      const key = getPastJobsAdvancedFiltersKey(companyId);
      if (hasAnyFilter) {
        setAdvancedFilters(filters);
        localStorage.setItem(key, JSON.stringify(stripEmptyPastJobsFilterFields(filters)));
      } else {
        setAdvancedFilters(null);
        localStorage.removeItem(key);
      }
      setPage(1);
    },
    [companyId],
  );

  const handleApplyDisplaySettings = useCallback((columns: TJobDisplayColumnSetting[]) => {
    setDisplayColumnSettings(columns);
  }, []);

  const { data: savedConditionsData } = useSavedConditionList(getPastJobsPageType(companyId));

  useEffect(() => {
    if (!savedConditionsData) return;
    const seededKey = getPastJobsDefaultSeededKey(companyId);
    if (readSessionFilterState<boolean>(seededKey)) return;
    writeSessionFilterState(seededKey, true);

    const fallback = savedConditionsData.data.find((item) => item.isDefault);
    if (!fallback) return;

    handleApplyAdvancedFilters({
      ...defaultPastJobsFilterValues,
      ...normalizePastJobsMultiSelectFields(normalizePastJobsDateFields(fallback.filters ?? {}) as Partial<Record<string, unknown>>),
    } as TPastJobsFilterForm);
    handleAppliedConditionNameChange(fallback.title);
    handleAppliedConditionIdChange(Number(fallback.id));
    const seededColumns = resolveSavedConditionColumns(fallback, defaultPastJobsDisplayColumns);
    setDisplayColumnSettings(seededColumns);
    localStorage.setItem(getPastJobsDisplayColumnsKey(companyId), JSON.stringify(seededColumns));
  }, [savedConditionsData]);

  const query = useMemo(
    () =>
      ({
        page,
        perPage: PER_PAGE,
        companyId,
        sorts: ["job.updatedAt|DESC"],
        ...(advancedFilters && {
          ...(advancedFilters.status.length > 0 ? { status: advancedFilters.status } : {}),
          ...(advancedFilters.jobName ? { jobName: advancedFilters.jobName } : {}),
          ...(advancedFilters.workplace ? { workplace: advancedFilters.workplace } : {}),
          ...(advancedFilters.totalAmount !== "" && advancedFilters.totalAmount != null ? { totalAmount: Number(advancedFilters.totalAmount) } : {}),
          ...(advancedFilters.caseCount !== "" && advancedFilters.caseCount != null ? { caseCount: Number(advancedFilters.caseCount) } : {}),
          ...(advancedFilters.recruitmentNumber !== "" && advancedFilters.recruitmentNumber != null
            ? { recruitmentNumber: Number(advancedFilters.recruitmentNumber) }
            : {}),
          ...(advancedFilters.endDateFrom ? { endDateFrom: dayjs(advancedFilters.endDateFrom).format(DATE_FORMAT_FOR_API) } : {}),
          ...(advancedFilters.endDateTo ? { endDateTo: dayjs(advancedFilters.endDateTo).format(DATE_FORMAT_FOR_API) } : {}),
        }),
      }) as Parameters<typeof useJobList>[0],
    [page, companyId, advancedFilters],
  );

  const { data, isLoading } = useJobList(query);

  const totalPage = data?.pagination?.totalPage ?? 1;
  const total = data?.pagination?.total ?? 0;

  return (
    <Modal isOpen={open} onClose={onClose} title="過去の求人一覧" width={1100} enableFooter={false} preventCloseBackdrop>
      <Stack spacing={3} mt={3}>
        <PastJobsTop
          total={total}
          page={page}
          perPage={PER_PAGE}
          onApplyAdvancedFilters={handleApplyAdvancedFilters}
          onApplyDisplaySettings={handleApplyDisplaySettings}
          appliedConditionName={appliedConditionName}
          appliedConditionId={appliedConditionId}
          onAppliedConditionNameChange={handleAppliedConditionNameChange}
          onAppliedConditionIdChange={handleAppliedConditionIdChange}
          currentDisplayColumns={lockedDisplayColumnSettings}
          currentAdvancedFilters={advancedFilters}
          companyId={companyId}
        />

        <PastJobsTable rows={data?.data ?? []} isLoading={isLoading} displayColumnSettings={lockedDisplayColumnSettings ?? defaultPastJobsDisplayColumns} />

        <Stack direction="row" alignItems="center">
          <Pagination totalPage={totalPage} page={page} onPageChange={setPage} />
        </Stack>
      </Stack>
    </Modal>
  );
};
